import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { supabase } from '../lib/supabase';

interface AuthContextType {
  user: any | null;
  isLoading: boolean;
  isLoggingOut: boolean;
  isAuthenticated: boolean;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<any | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  useEffect(() => {
    let isMounted = true;
    let sessionCheckInterval: NodeJS.Timeout;

    // CRITICAL: Check session immediately on mount (synchronous check)
    // This prevents delay/redirect to login when user already has valid session
    const checkInitialSession = async () => {
      try {
        const { data: { session }, error } = await supabase.auth.getSession();
        if (isMounted) {
          if (error || !session) {
            setUser(null);
            setIsLoading(false);
          } else {
            // Check if we have valid Supabase session in storage
            const allLocalStorageKeys = Object.keys(localStorage);
            const hasSupabaseAuthToken = allLocalStorageKeys.some(key => 
              key.startsWith('sb-') && key.includes('auth-token')
            );
            
            if (hasSupabaseAuthToken && session?.user) {
              setUser(session.user);
              setIsLoading(false);
            } else {
              setUser(null);
              setIsLoading(false);
            }
          }
        }
      } catch (error) {
        console.error('AuthContext: Initial session check error:', error);
        if (isMounted) {
          setUser(null);
          setIsLoading(false);
        }
      }
    };

    // Check session immediately
    checkInitialSession();

    // Periodic session validation for multi-device sync
    const startSessionValidation = () => {
      if (sessionCheckInterval) {
        clearInterval(sessionCheckInterval);
      }
      
      sessionCheckInterval = setInterval(async () => {
        if (!isMounted || !user) return;
        
        try {
          const { data: { session }, error } = await supabase.auth.getSession();
          if (error || !session) {
            if (isMounted) {
              setUser(null);
              setIsLoading(false);
            }
          } else {
            // Update user if session is valid but user state is stale
            if (isMounted && (!user || user.id !== session.user.id)) {
              setUser(session.user);
              setIsLoading(false);
            }
          }
        } catch (error) {
          console.error('AuthContext: Session validation error:', error);
          if (isMounted) {
            setUser(null);
            setIsLoading(false);
          }
        }
      }, 60000); // Check every 60 seconds (reduced frequency)
    };

    // Listen for auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (!isMounted) return;
      
      if (event === 'SIGNED_IN') {
        // Check if we have valid Supabase session in storage
        const allLocalStorageKeys = Object.keys(localStorage);
        const hasSupabaseAuthToken = allLocalStorageKeys.some(key => 
          key.startsWith('sb-') && key.includes('auth-token')
        );
        
        if (hasSupabaseAuthToken && session?.user) {
          setUser(session.user);
          setIsLoading(false);
        } else {
          setUser(null);
          setIsLoading(false);
        }
      } else if (event === 'SIGNED_OUT') {
        setUser(null);
        setIsLoading(false);
        setIsLoggingOut(false);
      } else if (event === 'TOKEN_REFRESHED') {
        setUser(session?.user || null);
        setIsLoading(false);
      } else if (event === 'USER_UPDATED') {
        setUser(session?.user || null);
        setIsLoading(false);
      } else if (event === 'INITIAL_SESSION') {
        // This event may fire after we already checked, so only update if needed
        if (session?.user && (!user || user.id !== session.user.id)) {
          setUser(session.user);
          setIsLoading(false);
        } else if (!session?.user) {
          setUser(null);
          setIsLoading(false);
        }
      }
    });

    // Start periodic session validation
    startSessionValidation();

    return () => {
      isMounted = false;
      subscription.unsubscribe();
      if (sessionCheckInterval) {
        clearInterval(sessionCheckInterval);
      }
    };
  }, []);

  const signOut = async () => {
    try {
      // Clear user state immediately for better UX
      setUser(null);
      setIsLoading(false);
      
      // Perform Supabase logout in the background without blocking the UI
      supabase.auth.signOut({ scope: 'local' }).catch(error => {
        console.error('AuthContext: Supabase signOut failed:', error);
      });
      
    } catch (error) {
      console.error('AuthContext: Error signing out:', error);
      // Fallback to direct Supabase logout if something else failed
      supabase.auth.signOut({ scope: 'local' }).catch(e => console.error('AuthContext: Fallback logout failed:', e));
    }
  };

  const value: AuthContextType = {
    user,
    isLoading,
    isLoggingOut,
    isAuthenticated: !!user,
    signOut,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
